import { useEffect, useState } from 'react'
import type { ReactNode } from 'react'
import { useProject } from '@/api/useProjects'
import styles from './ProjectThumbnail.module.css'

interface ProjectThumbnailProps {
    projectId: string
    widthMm: number
    heightMm: number
    placeholder?: ReactNode
}

const MM_TO_PX = 3.7795
const THUMB_WIDTH = 200

const thumbnailCache = new Map<string, string>()

export function ProjectThumbnail({ projectId, widthMm, heightMm, placeholder }: ProjectThumbnailProps) {
    const { data: project } = useProject(projectId)
    const [dataUrl, setDataUrl] = useState<string | null>(
        () => thumbnailCache.get(projectId) ?? null
    )
    const [generating, setGenerating] = useState(!thumbnailCache.has(projectId))
    const [failed, setFailed] = useState(false)

    useEffect(() => {
        if (thumbnailCache.has(projectId)) {
            setDataUrl(thumbnailCache.get(projectId)!)
            setGenerating(false)
            return
        }

        if (!project) return

        const firstPage = project.pages?.[0]
        if (!firstPage?.canvas_json || Object.keys(firstPage.canvas_json).length === 0) {
            setFailed(true)
            setGenerating(false)
            return
        }

        // If the canvas has an external Image (e.g. Canva import with GCS URL),
        // use that URL directly as thumbnail to avoid CORS / tainted-canvas issues.
        const externalImgUrl = extractExternalImageUrl(firstPage.canvas_json)
        if (externalImgUrl) {
            thumbnailCache.set(projectId, externalImgUrl)
            setDataUrl(externalImgUrl)
            setGenerating(false)
            return
        }

        let disposed = false

        const render = async () => {
            try {
                const fabric = await import('fabric')

                const docWidth = Math.round(widthMm * MM_TO_PX)
                const docHeight = Math.round(heightMm * MM_TO_PX)
                const thumbHeight = Math.round(THUMB_WIDTH * (docHeight / docWidth))
                const scale = THUMB_WIDTH / docWidth

                const el = document.createElement('canvas')
                el.width = THUMB_WIDTH
                el.height = thumbHeight

                // Use Canvas (not StaticCanvas) so IText and other interactive objects render
                // eslint-disable-next-line @typescript-eslint/no-explicit-any
                const canvas = new fabric.Canvas(el as any, {
                    width: THUMB_WIDTH,
                    height: thumbHeight,
                    backgroundColor: '#ffffff',
                    enableRetinaScaling: false,
                    renderOnAddRemove: false,
                })

                canvas.setViewportTransform([scale, 0, 0, scale, 0, 0])

                await canvas.loadFromJSON(firstPage.canvas_json)
                canvas.renderAll()

                if (disposed) {
                    canvas.dispose()
                    return
                }

                const url = canvas.toDataURL({ format: 'png', multiplier: 1 })
                canvas.dispose()

                // Clean up the wrapper elements Fabric creates
                const wrapper = el.parentElement
                if (wrapper) wrapper.remove()

                thumbnailCache.set(projectId, url)
                setDataUrl(url)
                setGenerating(false)
            } catch (err) {
                console.error('Thumbnail generation failed for project', projectId, err)
                if (!disposed) {
                    setFailed(true)
                    setGenerating(false)
                }
            }
        }

        render()

        return () => {
            disposed = true
        }
    }, [project, projectId, widthMm, heightMm])

    if (generating) {
        return (
            <div className={styles.container}>
                <div className={styles.skeleton} />
            </div>
        )
    }

    if (failed || (!generating && !dataUrl)) {
        return <>{placeholder ?? null}</>
    }

    return (
        <div className={styles.container}>
            <img
                className={styles.image}
                src={dataUrl!}
                alt="Vista previa del proyecto"
                draggable={false}
            />
        </div>
    )
}

/**
 * If the canvas JSON contains an Image object with an external URL (e.g. GCS),
 * return that URL so we can use it directly as a thumbnail without fabric.js rendering.
 * This avoids CORS / tainted-canvas issues with toDataURL().
 */
function extractExternalImageUrl(canvasJson: Record<string, unknown>): string | null {
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    const objects = (canvasJson as any)?.objects
    if (!Array.isArray(objects)) return null

    for (const obj of objects) {
        if (obj.type === 'Image' && typeof obj.src === 'string' && obj.src.startsWith('http')) {
            return obj.src
        }
    }
    return null
}
